import { Rss } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { SocialLinks } from '@/components/layout/SocialLinks';
import { PrivacyPolicyModal } from '@/components/site/PrivacyPolicyModal';
import { CONTACT_EMAIL, EVENTS_PAGE_ENABLED } from '@/config/site';
import { CATEGORIES } from '@/lib/categories';
import { openConsentSettings } from '@/lib/consent';

const LINK =
  'font-mono text-sm uppercase tracking-widest text-neutral-400 hover:text-neon-red transition-colors duration-150 focus:outline-none focus-visible:text-neon-red focus-visible:underline';

const FooterColumn: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <nav aria-label={title}>
    <h2 className="mb-4 font-mono text-xs font-bold uppercase tracking-[0.3em] text-white">
      {title}
    </h2>
    <ul className="space-y-3">{children}</ul>
  </nav>
);

export const Footer: React.FC = () => {
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

  return (
    <footer className="bg-black border-t border-neutral-800">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
        {/* Brand */}
        <div className="sm:col-span-2 space-y-5">
          <Link to="/" className="inline-block group focus:outline-none">
            <span className="block font-sans font-black text-2xl tracking-tighter text-white leading-none group-hover:text-neutral-300 transition-colors">
              HARD NEWS <span className="text-neon-red">HUNGARY</span>
            </span>
            <span className="block mt-1 text-[10px] font-mono text-neutral-400 tracking-[0.3em] uppercase">
              Go Hard or Go Home
            </span>
          </Link>
          <p className="max-w-sm font-body text-sm leading-relaxed text-neutral-400">
            Magyarország hardstyle és hard techno híroldala: hírek, interjúk, beszámolók és ajánlók
            a hazai és nemzetközi hard zenei színtérről.
          </p>
          <SocialLinks />
        </div>

        <FooterColumn title="Rovatok">
          {CATEGORIES.map((category) => (
            <li key={category.slug}>
              <Link to={category.path} className={LINK}>
                {category.title}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/blog" className={LINK}>
              Összes cikk
            </Link>
          </li>
        </FooterColumn>

        <FooterColumn title="Oldalak">
          <li>
            <Link to="/kereses" className={LINK}>
              Keresés
            </Link>
          </li>
          {EVENTS_PAGE_ENABLED && (
            <li>
              <Link to="/programajanlo" className={LINK}>
                Programajánló
              </Link>
            </li>
          )}
          <li>
            <Link to="/kapcsolat" className={LINK}>
              Kapcsolat
            </Link>
          </li>
          <li>
            <a href="/rss.xml" className={`${LINK} inline-flex items-center gap-2`}>
              <Rss className="h-4 w-4" aria-hidden="true" />
              RSS
            </a>
          </li>
        </FooterColumn>

        <FooterColumn title="Információ">
          <li>
            <button
              type="button"
              onClick={() => setIsPrivacyModalOpen(true)}
              className={`${LINK} text-left`}
            >
              Adatkezelési tájékoztató
            </button>
          </li>
          <li>
            <button type="button" onClick={openConsentSettings} className={`${LINK} text-left`}>
              Süti beállítások
            </button>
          </li>
          <li>
            <a href={`mailto:${CONTACT_EMAIL}`} className={`${LINK} normal-case tracking-normal`}>
              {/* Lets a narrow column break the address at the @ */}
              {CONTACT_EMAIL.split('@')[0]}
              <wbr />@{CONTACT_EMAIL.split('@')[1]}
            </a>
          </li>
        </FooterColumn>
      </div>

      <div className="border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between font-mono text-xs uppercase tracking-widest text-neutral-400">
          <p>&copy; {new Date().getFullYear()} Hard News Hungary. Minden jog fenntartva.</p>
          <p className="text-neon-red">Go Hard or Go Home</p>
        </div>
      </div>

      <PrivacyPolicyModal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
      />
    </footer>
  );
};
